import { css } from "lit";

import { animations } from "./global-header-animations.styles";

export const styles = css`
  :host {
    /* TODO Remove once color palette is updated */
    --color-neutral-00: #ffffff;
    --color-neutral-10: #f6f5f4;
    --color-neutral-20: #edeceb;
    --color-neutral-30: #e0dfdf;
    --color-neutral-40: #cccbca;
    --color-neutral-45: #767676;
    --color-neutral-50: #424242;
    --color-neutral-60: #363535;
    --color-neutral-70: #262626;
    --color-neutral-80: #1b1b1b;
    --color-neutral-90: #000000;
    --color-text-secondary: var(--color-neutral-45);

    --global-header-background-color: var(--color-neutral-00);
    --global-header-menu-tx-opened: 0;
    --global-header-menu-tx-closed: calc(-1 * var(--size-6));
    --global-header-menu-ty-opened: 0;
    --global-header-menu-ty-closed: 0;

    /* @link https://utopia.fyi/clamp/calculator?a=480,640,64—120 */
    --global-header-menu-inset-start: var(--size-10);
    /* @link https://utopia.fyi/clamp/calculator?a=480,640,0—10 */
    --global-header-menu-item-gap: clamp(0rem, -1.875rem + 6.25vh, 0.625rem);
    --global-header-submenu-item-gap: 0;

    --button-opacity-hover: 0.72;
    --button-opacity-active: 0.56;

    display: block;
  }

  .bypass {
    padding: var(--size-2);
    text-align: center;
    display: block;
    color: inherit;
  }

  .bypass:not(:focus) {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  header {
    position: relative;
    z-index: var(--z-80);
    display: grid;
    grid-auto-flow: column;
    grid-template-areas: "logo search control";
    grid-template-columns: 1fr;
    align-items: center;
    padding-inline: var(--size-1-5);
  }

  header[data-nav-open="true"]::before {
    content: "";
    background-color: var(--global-header-background-color);
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: var(--z-80);
  }

  ul {
    list-style-type: none;
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
  }

  li {
    border-bottom: 1px solid var(--color-neutral-20);
  }

  :is(a, button) {
    appearance: none;
    cursor: pointer;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    gap: var(--size-1);
    background-color: transparent;
    color: currentColor;
    border: none;
    padding-inline: 0;
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-medium);
    font-family: var(--font-sans);
    line-height: var(--line-height-none);
    -webkit-user-select: none;
    user-select: none;
    text-decoration: none;
    white-space: nowrap;
  }

  :is(a, button):focus-visible {
    outline: var(--a11y-outline);
  }

  :is(a, button):focus:not(:focus-visible) {
    outline: none;
  }

  .menu-item {
    padding-block: var(--size-2);
    transition: background-color var(--duration-short) var(--ease-in-out);
  }

  #main-menu .menu-item,
  #more-submenu .menu-item,
  #languages .util-item,
  #user-menu .util-item {
    justify-content: space-between;
  }

  .util-item {
    font-size: var(--font-size-lg);
    padding-block: var(--size-1-5);
    width: 100%;
  }

  .util-item > .util-item-ornament {
    display: none;
  }

  #container {
    background-color: var(--global-header-background-color);
    display: grid;
    grid-auto-flow: row;
    grid-template-areas:
      "primary"
      "languages"
      "user"
      "give";
    grid-template-rows: 1fr auto;
    transform: translateZ(0);
    position: fixed;
    inset-block-start: 0;
    inset-inline-start: 0;
    width: 100dvw;
    height: 100dvh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  #container .menu-item {
    width: 100%;
  }

  #logo {
    display: inline-flex;
    justify-self: start;
    grid-area: logo;
    z-index: var(--z-90);
    padding-block: var(--size-1-5);
    padding-inline: var(--size-1-5);
    line-height: var(--line-height-none);
  }

  #logo [variant="logo-mark"] {
    --logo-height: calc(var(--size-1) * 3.75);
    margin-block-start: 4px;
  }

  #logo [variant="word-mark"] {
    --logo-height: calc(var(--size-1) * 2.25);
    margin-block-start: 2px;
  }

  #primary-nav {
    grid-area: primary;
    align-self: start;
    padding-block: var(--global-header-menu-inset-start) var(--size-2);
  }

  #main-menu {
    list-style-type: none;
    display: flex;
    flex-direction: column;
    margin-inline: var(--size-3);
  }

  #search {
    grid-area: search;
    z-index: var(--z-90);
  }

  [data-nav-open="true"] #search {
    display: none;
  }

  #languages {
    --global-header-animation-delay: 30ms;
    grid-area: languages;
    margin-inline: var(--size-3);
    border-bottom: 1px solid var(--color-neutral-20);
  }

  #languages-submenu .menu-item {
    font-size: var(--font-size-md);
    padding-block: var(--size-1-5);
  }

  #give {
    --global-header-animation-delay: 90ms;
    grid-area: give;
    padding-block: var(--size-2) var(--size-3);
    padding-inline: var(--size-3);
  }

  #give > .menu-item {
    justify-content: center;
    border-radius: var(--radius-md);
    width: 100%;
    font-size: var(--font-size-lg);
    color: var(--color-neutral-00);
    background-color: var(--color-neutral-80);
    padding-block: var(--size-1-5);
  }

  #user-menu {
    --global-header-animation-delay: 60ms;
    grid-area: user;
    margin-inline: var(--size-3);
    border-bottom: 1px solid var(--color-neutral-20);
  }

  #menu-control {
    grid-area: control;
    z-index: var(--z-90);
  }

  bp-global-header-submenu {
    background-color: var(--color-white);
    position: absolute;
    z-index: var(--z-20);
    inset-block-start: 0;
    inset-inline-start: 0;
    box-sizing: border-box;
    width: 100dvw;
    height: 100dvh;
    padding-block: var(--global-header-menu-inset-start) var(--size-4);
  }

  bp-global-header-submenu ul {
    margin-inline: var(--size-3);
  }

  @media (hover: hover) {
    .menu-item:hover,
    .util-item:hover {
      animation-fill-mode: none;
      opacity: var(--button-opacity-hover);
    }

    #give .menu-item:hover {
      opacity: 1;
      background-color: var(--color-neutral-60);
    }
  }

  .menu-item:active,
  .util-item:active {
    opacity: var(--button-opacity-active);
  }

  #give .menu-item:active {
    background-color: var(--color-neutral-50);
  }

  @media (min-width: 1024px) {
    :host {
      --global-header-background-color: transparent;
      --global-header-menu-tx-opened: 0;
      --global-header-menu-tx-closed: 0;
      --global-header-menu-item-gap: var(--size-0-5);
      --global-header-submenu-item-gap: var(--size-0-5);
    }

    header {
      grid-auto-flow: column;
      grid-template-columns: min-content 1fr;
      gap: var(--size-1);
    }

    header::before {
      display: none;
    }

    li {
      border: none;
    }

    a,
    button {
      font-size: var(--font-size-md);
      border-radius: var(--radius-md);
    }

    .menu-item {
      padding: var(--size-1);
      gap: var(--size-0-5);
    }

    .util-item {
      padding-block: var(--size-1-5);
      padding-inline: var(--size-1);
    }

    .util-item > .util-item-label {
      display: none;
    }

    .util-item > .util-item-ornament {
      display: inline-flex;
    }

    #container {
      grid-template-areas: "primary search languages give user";
      grid-template-columns: 1fr minmax(0, 360px) auto auto auto;
      align-items: center;
      column-gap: var(--size-1);
      position: static;
      opacity: 1;
      overflow: initial;
      width: initial;
      height: initial;
    }

    #primary-nav {
      justify-items: start;
      align-self: unset;
      padding-block: unset;
    }

    #main-menu {
      min-height: unset;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      padding-block: 0;
      margin-inline: 0;
      column-gap: var(--size-1);
    }

    #languages {
      padding: unset;
      border: unset;
      margin-inline: 0;
    }

    #languages .submenu-indicator {
      display: none;
    }

    #languages .util-item,
    #user-menu .util-item {
      font-size: var(--font-size-md);
      border-radius: var(--radius-sm);
    }

    #user-menu {
      padding: unset;
      border: unset;
      display: inline-flex;
      align-items: center;
      margin-inline: 0;
    }

    #user-menu .submenu-indicator {
      display: none;
    }

    .submenu-indicator {
      --icon-size: var(--size-2);
      transform: rotate(90deg);
    }

    bp-global-header-submenu {
      background-color: transparent;
      width: unset;
      height: unset;
      padding: unset;
      opacity: 0;
      transform: translate3d(
        var(--global-header-submenu-tx-closed),
        var(--global-header-submenu-ty-closed),
        0
      );
    }

    bp-global-header-submenu ul {
      margin-inline: 0;
    }

    bp-global-header-submenu ul li a {
      border-radius: var(--radius-sm);
    }

    #menu-control {
      display: none;
    }

    #give {
      padding-block: unset;
      padding-inline: unset;
    }

    #give > .menu-item {
      justify-content: center;
      padding-block: var(--size-1);
      padding-inline: var(--size-1-5);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
    }

    @media (hover: hover) {
      .menu-item:hover {
        opacity: 1;
        background-color: var(--color-neutral-20);
      }

      .util-item:hover {
        opacity: 1;
        background-color: transparent;
      }
    }

    .menu-item:active {
      background-color: var(--color-neutral-10);
    }

    .util-item:active {
      background-color: transparent;
    }
  }

  /* Embed the animation stylesheet */
  ${animations}
`;
